import {
  Channel,
  ChannelNameTokens,
  ChannelTypes,
  deserialize,
  Expose,
  Serializable,
} from '@tokeet-frontend/tv3-platform'
import * as R from 'ramda'
import * as lodash from 'lodash'

export enum ConnectionTypes {
  Api = 'api',
  ImportedICal = 'imported',
  ExportedICal = 'exported',
}

export enum ConnectionStatusReasons {
  AuthorizationRevoked = 'authorization_revoked',
  OauthMissing = 'oauth_missing',
  RefreshTokenMissing = 'refresh_token_missing',
  HostSuspended = 'host_suspended',
}

export const defaultUndefinedRoomId = '....'

// Lifecycle of the most recent listing-import job for a connection, recorded by
// DBObjects::CMAccountOAuth::updateChannelImportStatus and polled while a job runs
// via GET /:channel/import/status/:channelId. 'skipped' means no job was queued at
// connect time because the account already has rentals (TV4-5888) — the listings
// were only fetched, for manual linking.
export interface ChannelImportStatus {
  status: 'queued' | 'in_progress' | 'done' | 'failed' | 'skipped'
  // Identifies the queue job the status belongs to; a newer job's writes supersede an
  // older job's on the backend, so consumers normally only ever see the latest job here.
  job_id?: string
  action?: string
  propertyId?: string | number
  total?: number
  // `imported` is retained by the backend for older clients. `processed` is the
  // property-wide number handled by the job, split into the outcome counts below.
  imported?: number
  processed?: number
  created_count?: number
  updated_count?: number
  already_existing_count?: number
  errors?: { listing?: string | number; error: string }[]
  // Connection-list responses omit the per-reservation errors but retain this aggregate
  // so a completed-with-warnings result remains visible after reopening the wizard.
  warning_count?: number
  error?: string
  updated?: number
}

export function isTerminalImportStatus(status: ChannelImportStatus | null | undefined): boolean {
  return !!status && (status.status === 'done' || status.status === 'failed' || status.status === 'skipped')
}

export class Connection extends Serializable<Connection> {
  @Expose({ name: 'id' })
  _id: string
  @Expose({ name: 'pkey' })
  id: string

  @Expose({ name: 'rental_id' })
  rentalId: string

  @Expose({ name: 'channel_rental_id' })
  channelRentalId: string

  @Expose({ name: 'ref_id' })
  refId: string

  @Expose({ name: 'rental_name' })
  rentalName: string

  @Expose({ name: 'channel_id' })
  channelId: string

  @Expose({ name: 'con_type' })
  conType: string

  @Expose({ name: 'status_changed' })
  statusChanged: number

  // Only set while status is 0. See DBObjects::CMAccountOAuth::disableProperty for the vocabulary.
  @Expose({ name: 'status_reason' })
  statusReason?: ConnectionStatusReasons | string

  // Set while the most recent listing-import attempt for this connection failed; cleared by
  // the next successful attempt. See DBObjects::CMAccountOAuth::updateChannelImportError
  // for the vocabulary ('enqueue_failed' | 'import_failed').
  @Expose({ name: 'import_error' })
  importError?: string

  @Expose({ name: 'import_error_time' })
  importErrorTime?: number

  // Richer counterpart of importError: the full lifecycle of the most recent
  // listing-import job (queued / in_progress / done / failed, with counts).
  @Expose({ name: 'import_status' })
  importStatus?: ChannelImportStatus

  status_text: string
  sendto: string
  skipped: number
  archived: number
  type: ConnectionTypes
  legacyId: string
  created: number
  lastsync: number
  lastavailpull: number
  lastavailpush: number
  lastratepush: number
  exported: number
  agent: string
  name: string
  source: string
  account: number
  url: string
  imported: number
  propertyId: string | number
  propertyLink?: string
  roomId: string | number
  status: number
  roomName: string
  propertyName: string
  linkDate: number
  attributes: any

  @Expose({ name: 'airbnb_status' })
  airbnbStatus?: string
  @Expose({ name: 'airbnb_listed' })
  airbnbListed?: boolean
  @Expose({ name: 'airbnb_listed_checked_at' })
  airbnbListedCheckedAt?: number
  @Expose({ name: 'airbnb_deleted' })
  airbnbDeleted?: boolean
  @Expose({ name: 'sync_paused' })
  syncPaused?: boolean
  @Expose({ name: 'sync_paused_reason' })
  syncPausedReason?: string
  // TV4-6062: the mapping points at a room the channel no longer has (e.g. deleted on the
  // booking.com extranet). The row exists only so the user can see and unlink the mapping.
  @Expose({ name: 'room_missing' })
  roomMissing?: boolean

  // TV4-6111: '....' is the placeholder row the API fabricates when a connection has no
  // saved rooms. It is not a linkable room -- linkRental rejects it server-side -- so
  // link/save controls disable themselves on such rows while the row itself stays visible.
  get isPlaceholderRoom(): boolean {
    return `${this.roomId ?? ''}` === defaultUndefinedRoomId
  }

  static deserialize(data: any): Connection {
    data.type =
      (R.hasIn('imported', data) && ConnectionTypes.ImportedICal) ||
      (R.hasIn('propertyId', data) && ConnectionTypes.Api) ||
      ConnectionTypes.ExportedICal
    data.pkey = getChannelConnectionId(data) // for api connections
    const item = deserialize<Connection>(Connection, data)
    item.channelId = item.channelId || item.channelRentalId?.replace(`-${item.rentalId}`, '')
    return item
  }
}

// BDC writes these timestamps server-side only after a scheduled operation actually completes.
// Other channels keep the legacy optimistic timestamps written when their request succeeds.
export function scheduledConnectionChanges(
  channelName: ChannelNameTokens,
  changes: Partial<Connection>
): Partial<Connection> {
  return channelName === ChannelNameTokens.BookingAPI ? {} : changes
}

// A sync timestamp only proves anything about the CURRENT rental mapping when it was
// written after the room was linked: the stamps are keyed to the channel room, so they
// survive an unlink/re-link while describing the previously linked rental (TV4-5906).
// The availability-push stamp is future-dated by 15s at its source (tokeetutils
// updateLogs), so a stamp written just BEFORE a re-link can carry a value just AFTER the
// new linkDate. Callers comparing lastavailpush pass this skew so such a stale stamp
// cannot pass as post-link; a genuine post-link push always clears it.
export const AVAIL_PUSH_STAMP_FUTURE_SKEW = 16

export function stampedSinceLink(connection: Connection, stamp: number | undefined, futureSkewSeconds = 0): boolean {
  return !!stamp && (!connection?.linkDate || stamp >= connection.linkDate + futureSkewSeconds)
}

// The listing wizard only automates (imports, availability waits, rate pushes) for a
// link that is actually being set up. Old links routinely lack post-link stamps (cron
// sweeps never write room stamps), and automating on them would fire real channel side
// effects — including overwriting Booking.com rates on an availability-only connection —
// from merely opening the wizard.
export function isRecentlyLinked(connection: Connection, maxAgeSeconds = 24 * 3600): boolean {
  return !!connection?.linkDate && Math.floor(Date.now() / 1000) - connection.linkDate <= maxAgeSeconds
}

// The connections reload that TV4-6062 dispatches straight after a link races the listing
// wizard's auto-chain: the rows are re-fetched before the sync workers have persisted
// anything, and replacing the store rows wholesale drops the optimistic lastavailpull /
// lastavailpush the chain has just recorded. The wizard then reads the Connecting step as
// incomplete and the linear stepper silently refuses to advance (TV4-5891). Keep the newer
// value per stamp, the same way loadConnectionsStatus merges the log rows.
export const CONNECTION_SYNC_STAMPS = ['lastavailpull', 'lastavailpush', 'lastratepush'] as const

export function withNewerSyncStamps(previous: Connection | undefined, next: Connection): Connection {
  if (!previous) {
    return next
  }
  const newer: Partial<Connection> = {}
  for (const stamp of CONNECTION_SYNC_STAMPS) {
    if (previous[stamp] && (!next[stamp] || previous[stamp] > next[stamp])) {
      newer[stamp] = previous[stamp]
    }
  }
  return R.isEmpty(newer) ? next : next.clone(newer)
}

export function getRealChannelId(connectionChannelId: string) {
  if (!connectionChannelId) return undefined
  return (connectionChannelId + '').split('.')[0]
}

export function getChannelConnectionId(conn: any): string {
  return conn.pkey
    ? conn.pkey
    : `${getRealChannelId(conn.channel_id || conn.channel || '')}-${
        conn.propertyId || conn.property || conn.rental_id
      }-${conn.roomId || conn.room}`
}

export enum ApiConnectionActivityTypes {
  AvailabilityPull = 'availpull',
  AvailabilityPush = 'availpush',
  RatesPush = 'ratespush',
  ListingPush = 'listingpush',
}

export class ApiConnectionActivityLog extends Serializable<ApiConnectionActivityLog> {
  account: number
  channel: string
  created: number
  message: string
  @Expose({ name: 'pkey' })
  id: string
  property: number | string
  request: string
  response: string
  room: number | string
  status: string
  ttl: number
  type: ApiConnectionActivityTypes

  static deserialize(data: any): ApiConnectionActivityLog {
    const item = deserialize<ApiConnectionActivityLog>(ApiConnectionActivityLog, data)
    item.type = normalizeLogType(item.type)
    item.status = item.status === 'failure' ? 'error' : item.status
    return item
  }
}

function normalizeLogType(type: string): ApiConnectionActivityTypes {
  switch (type) {
    case ApiConnectionActivityTypes.AvailabilityPull:
      return type
    case ApiConnectionActivityTypes.RatesPush:
    case 'ratepull':
    case 'ratepush':
      return ApiConnectionActivityTypes.RatesPush
    case ApiConnectionActivityTypes.AvailabilityPush:
    case 'availabilitypull':
      return ApiConnectionActivityTypes.AvailabilityPush
    case 'listingpull':
    case 'update-listing':
    case 'update-amenities':
    case 'update-policies':
    case 'update-fees-and-taxes':
    case 'update-media':
    case 'update-rooms':
      return ApiConnectionActivityTypes.ListingPush
    default:
      return type as ApiConnectionActivityTypes
  }
}

export function getChannelType(connectionType: ConnectionTypes) {
  switch (connectionType) {
    case ConnectionTypes.ExportedICal:
    case ConnectionTypes.ImportedICal:
      return ChannelTypes.ICal
    case ConnectionTypes.Api:
      return ChannelTypes.Auto
    default:
      return ''
  }
}

export function isChannelConnection(channel: Channel, conn: Connection) {
  const channelName = R.toLower(channel.name + '')
  const channelFriendlyName = R.toLower(channel.friendlyName + '')
  const connChannelType = getChannelType(conn.type)

  if (channel.id === getRealChannelId(conn.channelId)) return true
  if (!channel.type?.includes(connChannelType)) return false
  if (channelName === R.toLower(conn.source + '')) return true
  if (channelName === R.toLower(conn.name + '')) return true
  if (channelFriendlyName.includes(R.toLower(conn.source + ''))) return true

  return false
}

export function findConnectionChannel(conn: Connection, channels: Channel[]) {
  return R.find((c: Channel) => isChannelConnection(c, conn))(channels)
}

export class ImportedConnectionActivityLog extends Serializable<ImportedConnectionActivityLog> {
  @Expose({ name: 'pkey' })
  id: string

  account: number
  created: number
  message: string
  status: string
  ttl: number
  url: string

  expires: number

  static deserialize(data: any): ImportedConnectionActivityLog {
    data.expires = data.created + 7 * 24 * 3600 //  7 days after the created timestamp
    return deserialize(ImportedConnectionActivityLog, data)
  }
}
